import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar, MapPin, Gift, Copy, ArrowRight, Radio, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import LiveTrackingMap from "@/components/LiveTrackingMap";
import TeamChat from "@/components/TeamChat";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  matched: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-teal-50 text-teal-700 border-teal-200",
  in_progress: "bg-teal-50 text-teal-700 border-teal-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function ClientDashboard() {
  const nav = useNavigate();
  const { currency, user } = useApp();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs/mine");
      setJobs(data);
      const r = await api.get("/referral/mine"); setReferral(r.data);
    } catch { toast.error("Failed to load account overview"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upcoming = jobs.filter(j => ["pending", "matched", "active", "in_progress"].includes(j.status));
  const past = jobs.filter(j => ["completed", "cancelled"].includes(j.status));
  const completed = past.filter(j => j.status === "completed");
  const totalSpent = jobs.filter(j => j.payment_status === "paid").reduce((a, b) => a + b.total, 0);
  const reviews = jobs.filter(j => j.review);
  const avgRating = reviews.length ? (reviews.reduce((a, b) => a + b.review.rating, 0) / reviews.length).toFixed(1) : "—";

  // Next upcoming booking (soonest scheduled_at)
  const nextBooking = [...upcoming].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
  // Active booking for live tracking access
  const activeBooking = jobs.find(j => ["active", "in_progress"].includes(j.status));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Client Portal</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-1">Hello, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Here's a quick overview of your CLEANBAZ account.</p>
        </div>
        <Button onClick={() => nav("/services")} data-testid="dash-new-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Book new cleaning</Button>
      </div>

      {referral && (
        <Card className="mb-6 border-teal-200 bg-gradient-to-r from-teal-50 to-white" data-testid="referral-card">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-teal-600 flex items-center justify-center teal-glow">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-slate-900">Give $10, get $10</div>
                <div className="text-sm text-slate-600">Share your code — friends save on their first booking, you get $10 credit.</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {referral.credit > 0 && (
                <div className="text-right">
                  <div className="text-xs font-mono uppercase tracking-wider text-teal-600">Your credit</div>
                  <div className="font-display font-bold text-xl text-teal-600" data-testid="referral-credit">${referral.credit.toFixed(2)}</div>
                </div>
              )}
              <div className="bg-white border-2 border-dashed border-emerald-400 rounded-xl px-4 py-2 font-mono text-sm text-slate-900" data-testid="referral-code">{referral.code}</div>
              <Button size="sm" variant="outline" onClick={() => {
                const link = `${window.location.origin}/signup?ref=${referral.code}`;
                navigator.clipboard.writeText(link);
                toast.success("Referral link copied");
              }} data-testid="referral-copy"><Copy className="w-3.5 h-3.5 mr-1" />Copy link</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { l: "Upcoming", v: upcoming.length, c: "bg-teal-50 text-teal-700", tid: "summary-upcoming" },
          { l: "Completed", v: completed.length, c: "bg-blue-50 text-blue-700", tid: "summary-completed" },
          { l: "Total spent", v: `$${totalSpent.toFixed(0)}`, c: "bg-slate-100 text-slate-700", tid: "summary-total-spent" },
          { l: "Avg. rating given", v: avgRating, c: "bg-amber-50 text-amber-700", tid: "summary-avg-rating" },
        ].map((s, i) => (
          <Card key={i} className="border-slate-200" data-testid={s.tid}>
            <CardContent className="p-5">
              <div className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded inline-block ${s.c}`}>{s.l}</div>
              <div className="font-display font-extrabold text-3xl text-slate-900 mt-3">{s.v}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Next upcoming booking */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg text-slate-900">Next upcoming booking</h2>
          <button onClick={() => nav("/dashboard/bookings")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="view-all-bookings">
            View all bookings <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : !nextBooking ? (
          <Card><CardContent className="p-8 text-center text-slate-500" data-testid="no-next-booking">
            No upcoming cleanings.{" "}
            <button onClick={() => nav("/services")} className="text-teal-600 hover:underline font-semibold" data-testid="dash-empty-book-link">
              Browse the service catalog
            </button>{" "}to book one.
          </CardContent></Card>
        ) : (
          <Card className="border-slate-200 hover:shadow-md transition-all" data-testid={`next-booking-${nextBooking.id}`}>
            <CardContent className="p-6 grid md:grid-cols-4 gap-4 items-center">
              <div>
                <Badge className={`${STATUS_STYLES[nextBooking.status]} border font-mono text-[10px] uppercase`}>{nextBooking.status.replace(/_/g, " ")}</Badge>
                <div className="mt-2 font-display font-bold text-lg text-slate-900">
                  {nextBooking.bedrooms} bed · {nextBooking.bathrooms} bath{nextBooking.deep_clean && " · Deep"}
                </div>
                <div className="text-xs text-slate-500 flex items-start gap-1 mt-1">
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <div>
                    {nextBooking.street_address ? (
                      <>
                        <div>{nextBooking.street_address}{nextBooking.unit ? `, ${nextBooking.unit}` : ""}</div>
                        <div>{[nextBooking.city, nextBooking.state, nextBooking.postal_code].filter(Boolean).join(", ")}</div>
                      </>
                    ) : (<span>{nextBooking.postal_code}, {nextBooking.country}</span>)}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-slate-500">Scheduled</div>
                <div className="text-sm text-slate-900 mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(nextBooking.scheduled_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-slate-500">Total</div>
                <div className="font-display font-bold text-2xl text-teal-600 mt-1">{formatPrice(nextBooking.total, currency)}</div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => nav("/dashboard/bookings")} data-testid="manage-next-booking">
                  <ClipboardList className="w-3.5 h-3.5 mr-1" />Manage
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Live tracking quick access — only shown when a booking is actively being serviced */}
      {activeBooking && (
        <div className="mb-6" data-testid="live-tracking-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
            <h2 className="font-display font-bold text-lg text-slate-900 inline-flex items-center gap-2">
              <Radio className="w-4 h-4 text-teal-600" />Live tracking · booking in progress
            </h2>
          </div>
          <Card className="border-teal-200">
            <CardContent className="p-4">
              <LiveTrackingMap postal={activeBooking.postal_code || "10001"} eta={12} />
              <div className="mt-4">
                <TeamChat jobId={activeBooking.id} currentUserId={user?.id} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
